import { getImetaInfoFromImetaTag } from '@/lib/tag'
import { cn, parseYouTubeURL } from '@/lib/utils'
import { getImetaTagByUrl } from '@/services/media-upload.service'
import { TImetaInfo } from '@/types'
import { Event } from '@nostr/tools/wasm'
import { parse } from '@nostr/tools/nip27'
import { neventEncode, naddrEncode, nprofileEncode } from '@nostr/tools/nip19'
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { EmbeddedHashtag, EmbeddedMention, EmbeddedNote, EmbeddedWebsocketUrl } from './Embedded'
import Emoji from './Emoji'
import ExternalLink from './ExternalLink'
import ImageGallery from './ImageGallery'
import MediaPlayer from './MediaPlayer'
import WebPreview from './WebPreview'
import YoutubeEmbeddedPlayer from './YoutubeEmbeddedPlayer'
import YoutubeStaticEmbed from './YoutubeStaticEmbed'

export default function Content({
  event,
  content,
  className,
  mustLoadMedia,
  simpleEmbedId,
  clamp
}: {
  event?: Event
  content?: string
  className?: string
  mustLoadMedia?: boolean
  simpleEmbedId?: string
  clamp?: boolean
}) {
  const nodes = useMemo(() => {
    const nodes: ReactElement[] = []
    const allImages: (string | TImetaInfo)[] = []
    let imageIndex = 0

    let prevIsNewline = true
    let prevIsCandidate: string | false = false

    for (const block of parse(event ?? content ?? '')) {
      switch (block.type) {
        case 'text': {
          prevIsNewline = false

          if (block.text.startsWith('\n\n') && prevIsCandidate) {
            setPreviouysNodeAsPreview(prevIsCandidate)
            prevIsCandidate = false
          }
          if (block.text.endsWith('\n\n')) {
            prevIsNewline = true
          } else {
            prevIsCandidate = false
          }

          // preserve newlines even in otherwise-empty text blocks
          if (block.text.trim() === '' && !block.text.includes('\n')) continue

          nodes.push(<span key={nodes.length}>{block.text}</span>)
          break
        }
        case 'url': {
          prevIsCandidate = false
          if (prevIsNewline) {
            prevIsCandidate = block.url
          }
          prevIsNewline = false

          nodes.push(<ExternalLink key={nodes.length} url={block.url} />)
          break
        }
        case 'relay': {
          prevIsNewline = false
          prevIsCandidate = false
          nodes.push(<EmbeddedWebsocketUrl key={nodes.length} url={block.url} />)
          break
        }
        case 'image': {
          prevIsNewline = false
          prevIsCandidate = false
          if (nodes.length > 0 && (nodes[nodes.length - 1].type as any).name === 'ImageGallery') {
            nodes[nodes.length - 1] = (
              <ImageGallery
                key={nodes[nodes.length - 1].key}
                className="mt-2"
                images={allImages}
                start={(nodes[nodes.length - 1].props as any).start}
                end={imageIndex + 1}
                mustLoad={mustLoadMedia}
              />
            )
          } else {
            nodes.push(
              <ImageGallery
                key={nodes.length}
                className="mt-2"
                images={allImages}
                start={imageIndex}
                end={imageIndex + 1}
                mustLoad={mustLoadMedia}
              />
            )
          }

          let imeta: TImetaInfo | null = null
          if (event) {
            let tag = event.tags.find(
              ([k, ...vals]) => k === 'imeta' && vals.find((val) => val === `url ${block.url}`)
            )
            if (!tag) tag = getImetaTagByUrl(block.url)
            if (tag) {
              imeta = getImetaInfoFromImetaTag(tag, event.pubkey)
            }
          }

          if (imeta) allImages.push(imeta)
          else allImages.push(block.url)

          imageIndex++
          break
        }
        case 'video':
        case 'audio': {
          prevIsNewline = false
          prevIsCandidate = false
          nodes.push(
            <MediaPlayer
              key={nodes.length}
              className="mt-2"
              src={block.url}
              pubkey={event?.pubkey}
              mustLoad={mustLoadMedia}
            />
          )
          break
        }
        case 'reference': {
          prevIsNewline = false
          prevIsCandidate = false
          if ('id' in block.pointer) {
            nodes.push(
              <EmbeddedNote
                key={nodes.length}
                noteId={neventEncode(block.pointer)}
                className="mt-2"
                simple={simpleEmbedId === block.pointer.id}
              />
            )
          } else if ('identifier' in block.pointer) {
            nodes.push(
              <EmbeddedNote
                key={nodes.length}
                noteId={naddrEncode(block.pointer)}
                className="mt-2"
              />
            )
          } else {
            nodes.push(
              <EmbeddedMention key={nodes.length} userId={nprofileEncode(block.pointer)} />
            )
          }
          nodes.push(<span key={nodes.length}> </span>)
          break
        }
        case 'hashtag': {
          prevIsNewline = false
          prevIsCandidate = false
          nodes.push(<EmbeddedHashtag key={nodes.length} hashtag={block.value} />)
          nodes.push(<span key={nodes.length}> </span>)
          break
        }
        case 'emoji': {
          prevIsNewline = false
          prevIsCandidate = false
          nodes.push(<Emoji key={nodes.length} classNames={{ img: 'mb-1' }} emoji={block} />)
          break
        }
      }
    }

    if (prevIsCandidate) setPreviouysNodeAsPreview(prevIsCandidate)

    return nodes

    function setPreviouysNodeAsPreview(url: string) {
      const yt = parseYouTubeURL(url)
      if (yt) {
        if (clamp || yt.type === 'playlist' /* the normal renderer can't deal with playlists */) {
          nodes[nodes.length - 1] = (
            <YoutubeStaticEmbed key={nodes.length - 1} id={yt.id} type={yt.type} />
          )
        } else {
          nodes[nodes.length - 1] = (
            <YoutubeEmbeddedPlayer
              key={nodes.length - 1}
              url={url}
              className="mt-2"
              mustLoad={mustLoadMedia}
            />
          )
        }
      } else {
        nodes[nodes.length - 1] = <WebPreview key={nodes.length - 1} className="mt-2" url={url} />
      }
    }
  }, [event ?? content, simpleEmbedId])

  const contentRef = useRef<HTMLDivElement | null>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useEffect(() => {
    const el = contentRef.current
    if (!el) return

    function check() {
      setIsOverflowing(el!.scrollHeight > 300)
    }

    check()

    const observer = new ResizeObserver(check)
    observer.observe(el)

    return () => observer.disconnect()
  }, [])

  const inner = (
    <div
      className={cn('text-wrap break-words whitespace-pre-wrap', className)}
      ref={clamp ? contentRef : undefined}
    >
      {nodes}
    </div>
  )

  if (clamp) {
    return (
      <div className="relative overflow-hidden max-h-[300px]">
        {inner}
        <div
          className={`absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-b from-transparent to-background group-hover/hoverablenote:to-muted pointer-events-none ${
            isOverflowing ? 'opacity-100' : 'opacity-0'
          }`}
        >
          &nbsp;
        </div>
      </div>
    )
  }

  return inner
}
